import React from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { formatMoney } from '../../lib/formatMoney';

const GOLD = '#D4AF37';
const GOLD2 = '#E5C76B';

/**
 * One step between choosing how to pay and anything happening.
 *
 * Picking a method used to commit immediately — card threw the client
 * straight out to Stripe, Zelle and check recorded a payment — so a misclick
 * became a wrong payment. That happened to a real client last month.
 *
 * The confirm button NAMES THE METHOD. A button that says "Confirm" is worth
 * nothing here: the whole failure was someone confirming the wrong thing, and
 * you cannot misread "Yes — mail a check" as paying by card.
 */
const COPY = {
  card: {
    title: 'Pay by card?',
    next: (amount) => `You'll be taken to a secure card payment page to pay ${amount} now.`,
    confirm: 'Yes — pay by card',
  },
  zelle: {
    title: 'Pay by Zelle?',
    next: (amount) => `We'll show you the details to send ${amount} from your bank. Nothing is charged here.`,
    confirm: 'Yes — pay by Zelle',
  },
  check: {
    title: 'Mail a check?',
    next: (amount) => `We'll show you where to mail a check for ${amount}. Nothing is charged here.`,
    confirm: 'Yes — mail a check',
  },
};

export const ConfirmPaymentMethodDialog = ({ target, eventLabel, busy, onCancel, onConfirm }) => {
  const method = target?.method;
  const copy = COPY[method];
  if (!copy && target) return null;

  const amount = formatMoney(target?.amount ?? 0);

  return (
    // Closing the card — backdrop, Escape, the X — counts as going back, and
    // goes back is all it does: nothing has been sent or saved yet.
    <Dialog open={!!target} onOpenChange={(open) => { if (!open && !busy) onCancel(); }}>
      <DialogContent
        className="sm:max-w-[460px] text-white"
        style={{ background: '#0A0F1E', border: `1px solid ${GOLD}40` }}
        data-testid="cv-confirm-method-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[17px] font-semibold text-white/90 pr-10">
            {copy?.title}
          </DialogTitle>
        </DialogHeader>

        {/* 1 — the amount, large and unmissable */}
        <div
          className="text-[38px] sm:text-[44px] font-semibold leading-none tabular-nums"
          style={{ color: GOLD2 }}
          data-testid="cv-confirm-amount"
        >
          {amount}
        </div>

        {/* 2 — what they are paying, and which event */}
        <p className="text-[14px] text-white/60 -mt-1" data-testid="cv-confirm-what">
          {target?.label}
          {eventLabel ? ` · ${eventLabel}` : ''}
        </p>

        {/* 3 — the method, and exactly what happens next */}
        <p className="text-[15px] text-white/85 leading-[1.55]" data-testid="cv-confirm-next">
          {copy?.next(amount)}
        </p>

        {/* 4 — the buttons. Deliberately far apart: the confirm is full width,
                and "Go back" sits well below it so neither can be caught by a
                thumb aiming for the other. */}
        <div className="mt-2">
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="w-full rounded-[12px] py-4 text-[15.5px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70"
            style={{
              color: '#1a1500',
              background: `linear-gradient(180deg, ${GOLD2}, ${GOLD})`,
              boxShadow: '0 10px 26px -10px rgba(212,175,55,0.6)',
              cursor: busy ? 'not-allowed' : 'pointer',
              border: 'none',
            }}
            data-testid={`cv-confirm-${method}-btn`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {copy?.confirm}
          </button>

          <div className="text-center mt-7">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="text-[13.5px] text-white/45 hover:text-white/75 underline underline-offset-4 disabled:opacity-40 transition-colors bg-transparent border-0 p-0"
              data-testid="cv-confirm-go-back-btn"
            >
              Go back
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmPaymentMethodDialog;
